import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Button } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';

// Fix para los iconos de Leaflet
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Componente para manejar eventos de clic en el mapa
function MapEventsHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
}

// Componente para centrar el mapa suavemente
function ChangeView({ center }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

const LocationPicker = ({ onLocationChange }) => {
  const [position, setPosition] = useState([-17.7833, -63.1821]);
  const [loading, setLoading] = useState(false);

  // Función para ocultar el teclado
  const dismissKeyboard = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const updatePosition = useCallback((newPos) => {
    setPosition(newPos);
    if (onLocationChange) onLocationChange(newPos);
  }, [onLocationChange]);

  // Componente interno para manejar eventos del mapa
  const MapEventsHandler = () => {
    useMapEvents({
      click(e) {
        dismissKeyboard(); // Oculta teclado al tocar el mapa
        updatePosition([e.latlng.lat, e.latlng.lng]);
      },
      dragstart() {
        dismissKeyboard(); // Oculta teclado al empezar a mover el mapa
      },
      zoomstart() {
        dismissKeyboard(); // Oculta teclado al hacer zoom
      }
    });
    return null;
  };

  const handleGetCurrentLocation = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newPos = [pos.coords.latitude, pos.coords.longitude];
        updatePosition(newPos);
        setLoading(false);
      },
      () => setLoading(false),
      { enableHighAccuracy: true }
    );
  };

  const DraggableMarker = () => {
    const eventHandlers = useMemo(() => ({
      dragend(e) {
        const marker = e.target;
        const newPos = [marker.getLatLng().lat, marker.getLatLng().lng];
        updatePosition(newPos);
      },
    }), []);

    return (
      <Marker
        draggable={true}
        eventHandlers={eventHandlers}
        position={position}
      />
    );
  };

  return (
    <Box 
      // Captura clics fuera de los inputs dentro del contenedor del picker
      onClickCapture={dismissKeyboard} 
      sx={{ position: 'relative', height: '300px', width: '100%', borderRadius: 2, overflow: 'hidden', mb: 2 }}
    >
      <MapContainer center={position} zoom={15} style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        
        <MapEventsHandler />
        <ChangeView center={position} />
        <DraggableMarker />
      </MapContainer>
      
      {/* Botón de ubicación */}
      <Button
        variant="contained"
        size="small"
        disabled={loading}
        sx={{ 
          position: 'absolute', bottom: 16, right: 16, zIndex: 1000, 
          bgcolor: 'white', color: 'black', '&:hover': { bgcolor: '#f5f5f5' } 
        }}
        startIcon={<MyLocationIcon />}
        onClick={(e) => {
          e.stopPropagation(); // Evita conflictos
          dismissKeyboard();
          handleGetCurrentLocation();
        }}
      >
        {loading ? "Ubicando..." : "Mi ubicación"}
      </Button>
    </Box>
  );
};

export default LocationPicker;